import { createContext } from 'react';
import { useContext } from '@/hooks/useContext';

/**
 * RevenueCat entitlement identifiers (their `lookup_key`s in the RevenueCat dashboard).
 * The single source of truth lives in `@fytness/shared` (shared by the app + both backends);
 * re-exported here so existing imports from this module keep working.
 */
export { ENTITLEMENTS, type EntitlementId } from '@fytness/shared';

/**
 * RevenueCat offering `lookup_key` for the consumable AI credit packs. Kept
 * separate from the `default` (subscription) offering so consumables and
 * subscriptions stay cleanly split. The per-pack credit amount lives in this
 * offering's `metadata` (a `{ <package lookup_key>: <credits> }` map) — see
 * `creditPackages` on {@link PurchasesContextValue}.
 */
export const CREDITS_OFFERING = 'credits';

/**
 * Platform-neutral view of a purchasable package. Both the native
 * (`react-native-purchases`) and web (`@revenuecat/purchases-js`) providers map
 * their SDK package into this shape so the subscription screen never depends on a
 * platform-specific SDK type. `native` carries the original SDK object back to the
 * matching `purchasePackage` implementation.
 */
export interface SubscriptionPackage {
  /** RevenueCat package `lookup_key` (e.g. `$rc_monthly`). */
  identifier: string;
  /** Localized, formatted price string (e.g. `$4.99`). */
  priceString: string;
  /**
   * FYT credits this pack grants, read from the credits offering's `metadata`.
   * Set only for consumable credit packs (see `creditPackages`); `undefined` for
   * subscription packages.
   */
  credits?: number;
  /** Underlying SDK package object; opaque to consumers. */
  native: unknown;
}

export interface PurchaseResult {
  /** True when the purchase completed and entitlements were updated. */
  success: boolean;
  /** True when the user dismissed the purchase flow (not an error). */
  userCancelled: boolean;
}

export interface PurchasesContextValue {
  /** SDK configured and initial customer info / offerings loaded. */
  isReady: boolean;
  /** `pro` entitlement active. */
  isPro: boolean;
  /** `ai` entitlement active. */
  hasAiCoach: boolean;
  /** `unlimited` entitlement active. */
  isUnlimited: boolean;
  /**
   * Whether the user has an active subscription that is still set to auto-renew (not
   * cancelled). Drives the "cancel your subscription before deleting your account"
   * pre-check — lifetime / already-cancelled grants are false.
   */
  hasRenewingSubscription: boolean;
  /** Generic active-entitlement check by lookup key. */
  hasEntitlement: (id: string) => boolean;
  /** Active entitlement lookup keys. */
  activeEntitlements: string[];
  /**
   * Whether the active subscription can be managed / cancelled from the app. On
   * native this is available whenever there is a store subscription (the OS sheet
   * handles it even without a management URL); on web it needs the billing-portal
   * URL from RevenueCat.
   */
  canManageSubscriptions: boolean;
  /**
   * Open the platform's manage / cancel subscription UI — the native store
   * subscriptions sheet (iOS/Android) or the RevenueCat/Stripe billing portal (web).
   */
  openManageSubscriptions: () => Promise<void>;
  /** Packages of the current offering (empty when unavailable). */
  packages: SubscriptionPackage[];
  /**
   * Consumable AI credit packs from the `credits` offering (empty when
   * unavailable on this platform). Each carries its FYT `credits` amount and a
   * live `priceString`; purchase them via {@link purchasePackage}.
   */
  creditPackages: SubscriptionPackage[];
  /** Resolve a package by its `lookup_key`, or null when unavailable. */
  getPackage: (lookupKey: string) => SubscriptionPackage | null;
  /** Purchase a package. Resolves with `userCancelled: true` if the user backs out. */
  purchasePackage: (pkg: SubscriptionPackage) => Promise<PurchaseResult>;
  /** Restore prior purchases and refresh entitlements. */
  restorePurchases: () => Promise<void>;
  /** Force-refresh customer info + offerings from RevenueCat. */
  refresh: () => Promise<void>;
}

export const purchasesContext = createContext<
  PurchasesContextValue | undefined
>(undefined);
purchasesContext.displayName = 'Purchases';

export const usePurchases = () => useContext(purchasesContext);
